// groupMachines folds the cluster members (control, proxies, nodes) into one
// entry per physical machine: a box running several roles -- control + proxy +
// node colocated on one host -- becomes a single row labelled by its hostname.
// Members are grouped by reported hostname, else by address; the row shows all
// the roles it carries and the machine's stats (any colocated component measures
// the same box). Extracted from Admin.jsx so the folding is unit-tested.
const roleOrder = { control: 0, proxy: 1, node: 2 };

// How a machine row is labelled: its hostname, else its address, else the
// cluster name it registered under (all a pending node has).
export const machineLabel = (m) => m.hostname || m.address || m.name || "";

// A member that has not been approved (or was revoked) is not allowed to
// connect, so its silence is the expected state, not an outage. Members
// predating the approval column carry no status and count as approved.
const memberApproved = (m) => !m.status || m.status === "approved";

// Approval flips a member to "expected to report" the instant it is granted,
// but the member only redials every couple of seconds. Hold the row neutral for
// this long before calling it out as never reported.
export const joinGraceMs = 10000;

// How often to re-check the cluster while a member is expected to dial in.
export const joinPollMs = 1500;

// "" once the member has reported (or while it is not expected to), else
// "waiting" during the grace period after `since`, else "never".
export const machineJoinState = (m, since, now) => {
  if (m.lastSeen || !m.expected) return "";
  if (since && now - since < joinGraceMs) return "waiting";
  return "never";
};

// Which of a machine's roles may be revoked from the UI: never control (it is
// the registry doing the revoking), and nothing while a member on the machine
// is still awaiting approval, since that row offers Approve instead.
export const revocableRoles = (m) =>
  (m.unapproved || []).length > 0 || m.roles.includes("control") ? [] : m.roles.filter((r) => r !== "control");

// How many members are quiet when they should not be, i.e. the ones worth
// warning about above the cluster table.
export const quietMemberCount = (status) =>
  [...(status.nodes || []), ...(status.proxies || [])].filter((m) => m.stale && memberApproved(m)).length;

export const groupMachines = (status) => {
  const members = [
    ...(status.control ? [{ ...status.control, kind: "control" }] : []),
    ...(status.proxies || []).map((p) => ({ ...p, kind: "proxy" })),
    ...(status.nodes || []).map((n) => ({ ...n, kind: "node" })),
  ];
  const byKey = new Map();
  for (const m of members) {
    const host = (m.stats && m.stats.hostname) || "";
    const key = host || m.address || m.name;
    let g = byKey.get(key);
    if (!g) {
      // A member that has never connected reports neither a hostname nor an
      // address, so keep its cluster name as the last label/sort fallback.
      g = { key, hostname: host, address: m.address, name: m.name, roles: [], apps: 0, stats: {}, version: null, lastSeen: null, stale: true, expected: false, unapproved: [] };
      byKey.set(key, g);
    }
    if (!g.roles.includes(m.kind)) g.roles.push(m.kind);
    // A member awaiting approval (or revoked) holds no privileges; surface it on
    // the row so an admin can act without dropping to the CLI.
    if (m.status && m.status !== "approved" && (m.kind === "node" || m.kind === "proxy")) {
      g.unapproved.push({ kind: m.kind, name: m.name, status: m.status });
    }
    // The machine is only held to reporting if something on it is allowed to
    // connect; control always is.
    if (m.kind === "control" || memberApproved(m)) g.expected = true;
    if (m.kind === "node") g.apps += m.apps || 0;
    g.names = g.names || {};
    g.names[m.kind] = m.name;
    if (!g.hostname && host) g.hostname = host;
    if (!g.address) g.address = m.address;
    if (m.stats && (m.stats.memory_total_mb || m.stats.disk_total_mb)) g.stats = m.stats;
    if (m.version) g.version = m.version;
    // A member that has never connected (a pending node) serializes last_seen as
    // the zero time, which is still a truthy string -- ignore it, or the row
    // reports an age measured from year 1.
    const seen = m.last_seen && !m.last_seen.startsWith("0001-") ? m.last_seen : "";
    if (seen && (!g.lastSeen || seen > g.lastSeen)) g.lastSeen = seen;
    if (!m.stale) g.stale = false; // the machine is up if any of its components reported recently
  }
  const machines = [...byKey.values()];
  machines.forEach((g) => g.roles.sort((a, b) => roleOrder[a] - roleOrder[b]));
  machines.sort((a, b) => machineLabel(a).localeCompare(machineLabel(b)));
  return machines;
};
